import { useEffect, useMemo, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { MediaPickerModal } from "@/components/media/MediaPickerModal";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { SafeImage } from "@/components/ui/SafeImage";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import { useCategories } from "@/features/portfolio-categories/hooks";
import {
  useCreatePortfolioItem,
  useDeletePortfolioItem,
  usePortfolioItem,
  usePortfolioItems,
  useSetPrimaryPortfolioImage,
  useUpdatePortfolioItem,
} from "@/features/portfolio-items/hooks";
import type { PortfolioItem, PortfolioItemFilters, PortfolioItemInput, PortfolioStatus } from "@/features/portfolio-items/types";

const statuses: Array<PortfolioStatus | "ALL"> = ["ALL", "DRAFT", "PUBLISHED", "ARCHIVED"];

const emptyForm: PortfolioItemInput = {
  title: "",
  slug: "",
  description: "",
  clientName: "",
  year: null,
  categoryId: null,
  featured: false,
  sortOrder: 0,
  status: "DRAFT",
};

export function PortfolioItemsPage() {
  const currentUser = useCurrentUser();
  const categories = useCategories();
  const [filters, setFilters] = useState<PortfolioItemFilters>({ search: "", status: "ALL", categoryId: "" });
  const items = usePortfolioItems(filters);
  const createItem = useCreatePortfolioItem();
  const updateItem = useUpdatePortfolioItem();
  const deleteItem = useDeletePortfolioItem();
  const setPrimaryImage = useSetPrimaryPortfolioImage();
  const [drawerMode, setDrawerMode] = useState<"create" | "edit" | null>(null);
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const itemDetail = usePortfolioItem(selectedItem?.id ?? null);
  const [deleteTarget, setDeleteTarget] = useState<PortfolioItem | null>(null);
  const [form, setForm] = useState<PortfolioItemInput>(emptyForm);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = roleAtLeast(currentUser.data?.role, "ADMIN");
  const categoryOptions = categories.data?.filter((category) => category.isActive) ?? [];

  const sortedItems = useMemo(
    () => [...(items.data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title)),
    [items.data?.items],
  );

  useEffect(() => {
    if (!itemDetail.data || drawerMode !== "edit") return;
    setForm({
      title: itemDetail.data.title,
      slug: itemDetail.data.slug,
      description: itemDetail.data.description ?? "",
      clientName: itemDetail.data.clientName ?? "",
      year: itemDetail.data.year,
      categoryId: itemDetail.data.categoryId,
      featured: itemDetail.data.featured,
      sortOrder: itemDetail.data.sortOrder,
      status: itemDetail.data.status,
    });
  }, [drawerMode, itemDetail.data]);

  function openCreate() {
    setSelectedItem(null);
    setForm(emptyForm);
    setFiles([]);
    setSelectedMediaIds([]);
    setError(null);
    setDrawerMode("create");
  }

  function openEdit(item: PortfolioItem) {
    setSelectedItem(item);
    setForm({
      title: item.title,
      slug: item.slug,
      description: item.description ?? "",
      clientName: item.clientName ?? "",
      year: item.year,
      categoryId: item.categoryId,
      featured: item.featured,
      sortOrder: item.sortOrder,
      status: item.status,
    });
    setFiles([]);
    setSelectedMediaIds([]);
    setError(null);
    setDrawerMode("edit");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedItem(null);
    setForm(emptyForm);
    setFiles([]);
    setSelectedMediaIds([]);
    setError(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }

    if (drawerMode === "create") {
      createItem.mutate(
        { input: form, files, mediaIds: selectedMediaIds },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to create portfolio item."),
        },
      );
      return;
    }

    if (selectedItem) {
      updateItem.mutate(
        { id: selectedItem.id, input: form, files, mediaIds: selectedMediaIds },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to update portfolio item."),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteItem.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  const activeDetail = itemDetail.data ?? selectedItem;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Portfolio Items"
        description="Create and manage portfolio projects, publication state, categories, featured work, and gallery images."
        actions={<Button onClick={openCreate}>New Item</Button>}
      />

      <div className="grid gap-3 rounded-md border border-cyan-300/15 bg-black/25 p-4 lg:grid-cols-[1fr_14rem_16rem]">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-content-muted">Search</span>
          <input
            className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
            value={filters.search ?? ""}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder="Search by title"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-content-muted">Status</span>
          <select
            className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
            value={filters.status ?? "ALL"}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as PortfolioStatus | "ALL" }))}
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-content-muted">Category</span>
          <select
            className="mt-2 h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
            value={filters.categoryId ?? ""}
            onChange={(event) => setFilters((current) => ({ ...current, categoryId: event.target.value }))}
          >
            <option value="">All categories</option>
            {categoryOptions.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {items.isError && (
        <div className="rounded-md border border-red-300/30 bg-red-500/10 p-4 text-sm text-red-100">
          {items.error instanceof Error ? items.error.message : "Unable to load portfolio items."}
        </div>
      )}

      <AdminDataTable
        rows={sortedItems}
        getRowKey={(item) => item.id}
        emptyState={
          <EmptyState
            title={items.isLoading ? "Loading portfolio..." : "No portfolio items found"}
            description="Create the first portfolio item or adjust the current filters."
            action={!items.isLoading && <Button onClick={openCreate}>Create Portfolio Item</Button>}
          />
        }
        columns={[
          {
            key: "thumbnail",
            header: "Image",
            render: (item) => <Thumbnail item={item} />,
          },
          {
            key: "title",
            header: "Title",
            render: (item) => (
              <div>
                <p className="font-medium text-white">{item.title}</p>
                <p className="mt-1 text-xs text-content-muted">/{item.slug}</p>
              </div>
            ),
          },
          {
            key: "category",
            header: "Category",
            render: (item) => item.category?.name ?? <span className="text-content-muted">Unassigned</span>,
          },
          {
            key: "status",
            header: "Status",
            render: (item) => <PortfolioStatusBadges item={item} />,
          },
          {
            key: "sortOrder",
            header: "Sort",
            render: (item) => item.sortOrder,
          },
          {
            key: "actions",
            header: "Actions",
            className: "px-4 py-4 align-middle text-right",
            render: (item) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(item)}>
                  Edit
                </Button>
                {canDelete && (
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(item)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <AdminFormDrawer
        isOpen={drawerMode !== null}
        title={drawerMode === "create" ? "New Portfolio Item" : "Edit Portfolio Item"}
        description="Gallery uploads use the existing multipart portfolio image endpoint. The primary image is used as the thumbnail."
        onClose={closeDrawer}
      >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField label="Title" value={form.title} onChange={(value) => setForm((current) => ({ ...current, title: value }))} required />
          <TextField label="Slug" value={form.slug ?? ""} onChange={(value) => setForm((current) => ({ ...current, slug: value }))} />
          <label className="block">
            <span className="text-sm text-content-secondary">Full Description</span>
            <textarea
              className="mt-2 min-h-32 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
              value={form.description ?? ""}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              maxLength={5000}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Client / Server Name"
              value={form.clientName ?? ""}
              onChange={(value) => setForm((current) => ({ ...current, clientName: value }))}
            />
            <TextField
              label="Project Year"
              type="number"
              value={form.year ? String(form.year) : ""}
              onChange={(value) => setForm((current) => ({ ...current, year: value ? Number(value) : null }))}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm text-content-secondary">Category</span>
              <select
                className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
                value={form.categoryId ?? ""}
                onChange={(event) => setForm((current) => ({ ...current, categoryId: event.target.value || null }))}
              >
                <option value="">Unassigned</option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-content-secondary">Status</span>
              <select
                className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
                value={form.status ?? "DRAFT"}
                onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as PortfolioStatus }))}
              >
                <option value="DRAFT">Draft</option>
                <option value="PUBLISHED">Published</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Sort Order"
              value={String(form.sortOrder ?? 0)}
              type="number"
              onChange={(value) => setForm((current) => ({ ...current, sortOrder: Number(value) }))}
            />
            <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3 sm:mt-7">
              <input
                className="size-4 accent-cyan-300"
                type="checkbox"
                checked={Boolean(form.featured)}
                onChange={(event) => setForm((current) => ({ ...current, featured: event.target.checked }))}
              />
              <span className="text-sm text-content-secondary">Featured item</span>
            </label>
          </div>
          <div className="rounded-md border border-amber-300/25 bg-amber-300/10 p-3 text-xs leading-5 text-amber-100">
            TODO: short description, exact project date, and tags need database/API fields before they can be persisted. Current API stores full description and project year.
          </div>
          <ImageUploadField
            label="Gallery Images"
            helperText="Upload one or more images. The first image on a new item becomes the thumbnail/primary image."
            multiple
            onChange={(fileList) => setFiles(fileList ? Array.from(fileList) : [])}
          />
          <div className="rounded-md border border-cyan-300/15 bg-black/20 p-3">
            <p className="text-sm text-content-secondary">Media Library Images</p>
            <p className="mt-2 text-xs text-content-muted">{selectedMediaIds.length} selected from media library</p>
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setMediaPickerOpen(true)}>Choose Media</Button>
              {selectedMediaIds.length > 0 && <Button type="button" variant="ghost" onClick={() => setSelectedMediaIds([])}>Clear</Button>}
            </div>
          </div>
          {activeDetail?.images && activeDetail.images.length > 0 && (
            <div>
              <p className="mb-2 text-sm text-content-secondary">Existing Gallery</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {activeDetail.images.map((image) => (
                  <div key={image.id} className="rounded-md border border-cyan-300/15 bg-black/30 p-2">
                    <SafeImage src={image.url} alt={image.alt || activeDetail.title} fallbackLabel="No image" className="aspect-video w-full rounded object-cover" />
                    <Button
                      className="mt-2 w-full"
                      size="sm"
                      variant={image.isPrimary ? "secondary" : "ghost"}
                      type="button"
                      disabled={image.isPrimary || setPrimaryImage.isPending}
                      onClick={() => setPrimaryImage.mutate({ itemId: activeDetail.id, imageId: image.id })}
                    >
                      {image.isPrimary ? "Thumbnail" : "Set Thumbnail"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && <p className="rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" disabled={createItem.isPending || updateItem.isPending}>
              {createItem.isPending || updateItem.isPending ? "Saving..." : "Save Item"}
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      <MediaPickerModal
        isOpen={mediaPickerOpen}
        multiple
        onClose={() => setMediaPickerOpen(false)}
        onSelect={(items) => setSelectedMediaIds(items.map((item) => item.id))}
      />

      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        title="Delete Portfolio Item"
        description={`Soft delete "${deleteTarget?.title ?? "this item"}"?`}
        isPending={deleteItem.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function Thumbnail({ item }: { item: PortfolioItem }) {
  const image = item.images[0];
  if (!image) {
    return <div className="grid size-16 place-items-center rounded-md border border-cyan-300/15 bg-black/40 text-xs text-content-muted">No image</div>;
  }
  return <SafeImage src={image.url} alt={image.alt || item.title} fallbackLabel="No image" className="size-16 rounded-md object-cover" />;
}

function PortfolioStatusBadges({ item }: { item: PortfolioItem }) {
  return (
    <div className="flex flex-wrap gap-2">
      <StatusBadge tone={item.status === "PUBLISHED" ? "active" : item.status === "ARCHIVED" ? "warning" : "muted"}>{item.status}</StatusBadge>
      {item.status === "PUBLISHED" && <StatusBadge tone="active">Published</StatusBadge>}
      {item.featured && <StatusBadge tone="warning">Featured</StatusBadge>}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number";
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
      />
    </label>
  );
}
